<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { examModule } from '@/store/exam/module'
import UIKit from '@pocketprep/ui-kit'

type TTrimmedPrep = {
    prep: string
    trimmedPrep: string
}

type TPrepFilter = {
    prep: string[]
    trimmedPrep: string[]
}

type TStatusesFilter = {
    statuses: string[]
    trimmedStatuses: string[]
}

type TStudent = {
    studentId: string
    prep: string[]
    status: string
}

const { CheckboxOption, Icon } = UIKit

const props = withDefaults(defineProps<{
    settingsStudents: TStudent[]
    activeTab: string
    statusFiltersToRemove?: string[]
    prepFiltersToRemove?: string[]
}>(), {
    statusFiltersToRemove: () => [],
    prepFiltersToRemove: () => [],
})

const emit = defineEmits<{
    close: []
    prepFilterChange: [obj: TPrepFilter]
    statusesFilterChange: [obj: TStatusesFilter]
}>()

const dropdown = ref<HTMLElement | null>(null)
const trimmedPrepSet = ref(new Set<string>())
const showPrepOptions = ref(true)
const trimmedStatusesSet = ref(new Set<string>())
const showStatusOptions = ref(false)

const trimmedPrep = computed(() => {
    return [ ...trimmedPrepSet.value ]
})
const trimmedStatuses = computed(() => {
    return [ ...trimmedStatusesSet.value ]
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const getStudentCountForPrep = (prepName: string) => {
    return props.settingsStudents.filter(s => s.prep.includes(prepName)).length
}

const getStudentCountForStatus = (status: string) => {
    return props.settingsStudents.filter(s => s.status === status
        || (s.status === 'Mixed' && status !== 'Archived')
    ).length
}

const prepOptions = computed(() => {
    const checkboxOptions: TTrimmedPrep[] = []
    const currentOrgExamsVal = currentOrgExams.value
    if (currentOrgExamsVal) {
        Object.values(currentOrgExamsVal).forEach(coe => {
            if (!checkboxOptions.find(co => co.trimmedPrep === coe.displayName)) {
                checkboxOptions.push({
                    prep: `${coe.displayName} (${getStudentCountForPrep(coe.displayName)})`,
                    trimmedPrep: coe.displayName,
                })
            }
        })
        return checkboxOptions
    }

    return checkboxOptions
})

const statusOptions = computed(() => {
    return props.activeTab === 'student'
        ? [
            {
                status: `Active (${getStudentCountForStatus('Active')})`,
                trimmedStatus: 'Active',
            },
            {
                status: `Pending (${getStudentCountForStatus('Pending')})`,
                trimmedStatus: 'Pending',
            },
        ]
        : []
})

const stringHasSpaces = (val: string) => {
    return val.includes(' ')
}

const emitClose = () => {
    emit('close')
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        emitClose()
    }
}

const clickListener = (e: MouseEvent) => {
    const targetEl = e.target as HTMLElement
    const dropdownEl = dropdown.value as HTMLElement
    if (!dropdownEl?.contains(targetEl) && !targetEl?.closest('.students__filter')
        && document.body.contains(targetEl)) {
        emitClose()
    }
}

const toggleMenuDropdown = (e: Event, menu: 'prep' | 'status') => {
    if (menu === 'prep') {
        showPrepOptions.value = !showPrepOptions.value
    } else if (menu === 'status') {
        showStatusOptions.value = !showStatusOptions.value
    }
    const dropdownEl = dropdown.value as HTMLElement
    const targetEl = e && (e.target as HTMLElement)

    nextTick(() => {
        if (!dropdownEl) {
            return
        }
        if (showStatusOptions.value) {
            if (targetEl) {
                const containerTop = dropdownEl.getBoundingClientRect().top
                const targetTop = targetEl.getBoundingClientRect().top
                const offset = targetTop - containerTop
                dropdownEl.scrollTop += offset
            } else {
                dropdownEl.scrollTop = dropdownEl.scrollHeight
            }
        }
    })
}

const togglePrepOption = (trimmedPrepName: string, value: boolean) => {
    if (value) {
        trimmedPrepSet.value.add(trimmedPrepName)
    } else {
        trimmedPrepSet.value.delete(trimmedPrepName)
    }
}

const toggleStatusOptions = (trimmedStatusesVal: string, value: boolean) => {
    if (value) {
        trimmedStatusesSet.value.add(trimmedStatusesVal)
    } else {
        trimmedStatusesSet.value.delete(trimmedStatusesVal)
    }
}

onMounted(() => {
    document.addEventListener('keydown', keydownListener)
    document.addEventListener('click', clickListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', keydownListener)
    document.removeEventListener('click', clickListener)
})

watch(() => props.activeTab, () => {
    trimmedPrepSet.value.clear()
    trimmedStatusesSet.value.clear()
})

watch(() => props.prepFiltersToRemove, () => {
    props.prepFiltersToRemove.forEach(pf => trimmedPrepSet.value.delete(pf))
})

watch(() => props.statusFiltersToRemove, () => {
    props.statusFiltersToRemove.forEach(sf => trimmedStatusesSet.value.delete(sf))
})

watch(trimmedPrep, () => {
    const prepMap = new Map(prepOptions.value.map(o => [ o.trimmedPrep, o.prep ]))
    const prep = trimmedPrep.value.map(tp => prepMap.get(tp) || tp)
    emit('prepFilterChange', {
        prep,
        trimmedPrep: trimmedPrep.value,
    })
}, { deep: true })

watch(trimmedStatuses, () => {
    const statusMap = new Map(statusOptions.value.map(o => [ o.trimmedStatus, o.status ]))
    const statuses = trimmedStatuses.value.map(ts => statusMap.get(ts) || ts)
    emit('statusesFilterChange', {
        statuses,
        trimmedStatuses: trimmedStatuses.value,
    })
}, { deep: true })
</script>

<template>
    <div
        ref="dropdown"
        class="filter-dropdown"
    >
        <div
            class="filter-dropdown__label"
            :class="{ 'filter-dropdown__label--active': showPrepOptions }"
            tabindex="0"
            :aria-label="`${showPrepOptions ? 'Hide' : 'Show'} question type filters`"
            @click="toggleMenuDropdown($event, 'prep')"
            @keypress.enter="toggleMenuDropdown($event, 'prep')"
            @mousedown.prevent
        >
            <span>Filter by Prep</span>
            <Icon
                type="accordionArrow"
                class="filter-dropdown__arrow"
            />
        </div>
        <div
            v-if="showPrepOptions"
            aria-live="polite"
            class="filter-dropdown__prep-options"
        >
            <CheckboxOption
                v-for="prepOption in prepOptions"
                :key="prepOption.prep"
                :label="prepOption.prep"
                class="filter-dropdown__prep-option"
                :class="{
                    'filter-dropdown__prep-option--force-break': !stringHasSpaces(prepOption.prep)
                }"
                :modelValue="trimmedPrep.includes(prepOption.trimmedPrep)"
                @update:modelValue="togglePrepOption(prepOption.trimmedPrep, $event)"
            />
        </div>
        <div
            v-if="statusOptions.length"
            class="filter-dropdown__label"
            :class="{ 'filter-dropdown__label--active': showStatusOptions }"
            tabindex="0"
            :aria-label="`${showPrepOptions ? 'Hide' : 'Show'} status filters`"
            @click="toggleMenuDropdown($event, 'status')"
            @keypress.enter="toggleMenuDropdown($event, 'status')"
            @mousedown.prevent
        >
            <span>Filter by Status</span>
            <Icon
                type="accordionArrow"
                class="filter-dropdown__arrow"
            />
        </div>
        <div
            v-if="showStatusOptions"
            aria-live="polite"
            class="filter-dropdown__status-options"
        >
            <CheckboxOption
                v-for="statusOption in statusOptions"
                :key="statusOption.status"
                :label="statusOption.status"
                class="filter-dropdown__status-option"
                :class="{
                    'filter-dropdown__status-option--force-break': !stringHasSpaces(statusOption.status)
                }"
                :modelValue="trimmedStatuses.includes(statusOption.trimmedStatus)"
                @update:modelValue="toggleStatusOptions(statusOption.trimmedStatus, $event)"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.filter-dropdown {
    background: $white;
    border-radius: 8px;
    box-shadow: 0 6px 22px 0 rgba($brand-black, 0.1);
    padding: 9px 20px;
    box-sizing: border-box;
    width: 100%;
    font-weight: 500;
    max-height: 305px;
    overflow: auto;
    scroll-behavior: smooth;

    &__label {
        font-size: 10px;
        letter-spacing: 0.2px;
        line-height: 30px;
        font-weight: 600;
        cursor: pointer;
        color: $steel;
        display: flex;
        align-items: center;
        text-transform: uppercase;
        outline: none;
        position: relative;

        &--active svg {
            transform: rotate(180deg);
        }

        &:focus {
            &::before {
                content: '';
                left: -6px;
                top: -2px;
                width: calc(100% + 12px);
                height: 100%;
                border: 1px solid $brand-blue;
                position: absolute;
                border-radius: 6px;
            }
        }

        span {
            margin-right: 6px;
        }

        svg {
            color: $brand-blue;
            width: 7px;
        }
    }

    &__options {
        margin-bottom: 6px;
    }

    &__prep-option {
        &--force-break {
            word-break: break-all;
        }
    }
}
</style>
